import React, { useRef, useEffect } from 'react';
import { Mode, ModelId, KnowledgeBaseId } from '../../types';
import { AvailableModel, KnowledgeBase } from '../../services/ai/fastApiBackend';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Settings } from 'lucide-react';

interface ChatInputProps {
  input: string;
  setInput: (value: string) => void;
  onSubmit: () => void;
  isStreaming: boolean;
  mode: Mode;
  onClearChat: () => void;
  selectedModel: ModelId;
  setSelectedModel: (model: ModelId) => void;
  selectedKnowledgeBase: KnowledgeBaseId;
  setSelectedKnowledgeBase: (knowledgeBase: KnowledgeBaseId) => void;
  availableModels: AvailableModel[];
  knowledgeBases: KnowledgeBase[];
  isDisabled?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ 
  input, 
  setInput, 
  onSubmit, 
  isStreaming, 
  mode,
  onClearChat,
  selectedModel,
  setSelectedModel,
  selectedKnowledgeBase,
  setSelectedKnowledgeBase,
  availableModels,
  knowledgeBases,
  isDisabled = false
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Set model to "none" if no available models
  useEffect(() => {
    if (availableModels.length === 0 && selectedModel !== 'none') {
      setSelectedModel('none' as ModelId);
    }
  }, [availableModels, selectedModel, setSelectedModel]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isStreaming && !isDisabled) {
        onSubmit();
      }
    }
  };

  // Create model options with "none" option when no models are available
  const modelOptions = availableModels.length > 0 
    ? availableModels.map(m => ({ id: m.id, label: m.name }))
    : [{ id: 'none' as ModelId, label: 'None' }];
  
  const knowledgeBaseOptions = [
    { id: 'none' as KnowledgeBaseId, label: 'None' },
    ...knowledgeBases.map(kb => ({ id: kb.id as KnowledgeBaseId, label: kb.name }))
  ];

  const selectedModelLabel = modelOptions.find(m => m.id === selectedModel)?.label || selectedModel;
  const selectedKnowledgeBaseLabel = knowledgeBaseOptions.find(k => k.id === selectedKnowledgeBase)?.label || selectedKnowledgeBase;

  return (
    <div className="flex items-end gap-2 border-t p-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            title="Settings"
            disabled={isStreaming || isDisabled}
            className="h-8 w-8 flex-shrink-0 self-center"
          >
            <Settings className="h-4 w-4 m-auto" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-xs font-normal text-muted-foreground">
              Model: {selectedModelLabel}
            </div>
            <div className="text-xs font-normal text-muted-foreground">
              Knowledge Base: {selectedKnowledgeBaseLabel}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={onClearChat} 
            disabled={isStreaming || isDisabled}
          >
            Clear History
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Model</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={selectedModel}
            onValueChange={(value) => setSelectedModel(value as ModelId)}
          >
            {modelOptions.map(option => (
              <DropdownMenuRadioItem
                key={option.id}
                value={option.id}
                disabled={isStreaming || isDisabled}
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Knowledge Base</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={selectedKnowledgeBase}
            onValueChange={(value) => setSelectedKnowledgeBase(value as KnowledgeBaseId)}
          >
            {knowledgeBaseOptions.map(option => (
              <DropdownMenuRadioItem
                key={option.id}
                value={option.id}
                disabled={isStreaming || isDisabled}
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Textarea
        ref={textareaRef}
        placeholder={mode === "compose" ? "What do you want to write?" : "Ask me anything..."}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
          }
        }}
        onKeyDown={handleKeyDown}
        className="min-h-[3rem] max-h-[12rem] resize-none"
        rows={1}
        disabled={isStreaming || isDisabled}
      />
    </div>
  );
};